// Parses a child conversation's `topic` string into a display-ready agent
// name + optional target, and formats a compact relative age ("2m"/"3h"/"1d").
// Pure, no I/O, table-testable. Mirrors the jobLedger.util.ts style: plain
// exported functions, file-level comment, no class/config/DI.
//
// The Subagents roster diverges from the Jobs ledger by leading with a HUMAN
// actor name (prettified) instead of a capability/source name; this helper
// owns that prettification so SubagentRow stays presentational.

export interface ParsedSubagentTopic {
  /** Human, Title-Cased agent name for the row's primary line. Always present. */
  agentLabel: string;
  /** Cleaned, display-ready target host/path. Undefined when there is no target. */
  target?: string;
}

const AGENT_ON_RE = /^Agent\s+(\S+)\s+on\s+\S+\s+(.+)$/;
const SUBAGENT_RE = /^Subagent:\s*(.+)$/i;

/**
 * Title-Cases a single `-`/`_`-separated token group, joining segments with
 * spaces. `generic_scanner` -> `Generic Scanner`, `aurelian` -> `Aurelian`.
 */
function titleCase(name: string): string {
  return name
    .split(/[-_]/)
    .filter(Boolean)
    .map(
      segment =>
        segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
    )
    .join(' ');
}

/**
 * Prettifies a raw agent name into a human label. Applied in order; first
 * match wins: strip trailing `-agent`, special-case known agents, else
 * Title-Case the segments. Falls back to the original on empty result.
 */
function prettyName(agentName: string): string {
  const stripped = agentName.replace(/-agent$/i, '');
  const lower = stripped.toLowerCase();

  if (lower === 'hannibal') return 'Hannibal (Planner)';
  if (lower === 'generic-exploiter' || lower === 'generic_exploiter') {
    return 'Exploiter';
  }

  const titled = titleCase(stripped);
  return titled || agentName;
}

/**
 * Cleans a raw target key into a display host/path. Takes the last `#`-segment,
 * strips the URL scheme and a single trailing slash, and keeps any path.
 * Returns undefined when nothing meaningful remains (so the row omits line 2).
 */
function prettyTarget(rawTargetKey: string): string | undefined {
  const lastSegment = rawTargetKey.split('#').filter(Boolean).pop();
  if (!lastSegment) return undefined;

  const cleaned = lastSegment.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  return cleaned || undefined;
}

/**
 * Parses a child conversation's `topic` into a display name + optional target.
 */
export function parseSubagentTopic(
  topic?: string,
  uuid?: string
): ParsedSubagentTopic {
  const trimmed = topic?.trim() ?? '';

  if (trimmed) {
    const agentOnMatch = AGENT_ON_RE.exec(trimmed);
    if (agentOnMatch) {
      return {
        agentLabel: prettyName(agentOnMatch[1]),
        target: prettyTarget(agentOnMatch[2]),
      };
    }

    const subagentMatch = SUBAGENT_RE.exec(trimmed);
    if (subagentMatch) {
      return { agentLabel: prettyName(subagentMatch[1].trim()) };
    }

    // Non-empty but unparseable: surface the raw topic (CSS truncates it).
    return { agentLabel: trimmed };
  }

  // Empty/undefined topic -> fall back to the uuid's last 8 chars.
  return { agentLabel: `Agent ${(uuid ?? '').slice(-8)}`.trim() };
}

/**
 * Formats the elapsed time between `timestamp` and `now` as a compact label:
 * `<1m` -> "now", minutes -> "Nm", hours -> "Nh", days -> "Nd". Pure: `now` is
 * injected so it is table-testable. Returns undefined for missing/invalid input
 * (so the caller can omit the " · time" suffix).
 */
export function formatCompactAge(
  timestamp?: string,
  now: number = Date.now()
): string | undefined {
  if (!timestamp) return undefined;

  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return undefined;

  const diffMs = Math.max(0, now - then);
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  return `${days}d`;
}
